import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";

export interface Laboratorio {
  id: string;
  user_id: string;
  paciente: string;
  procedimento: string;
  laboratorio: string;
  data_envio: string;
  data_retorno: string;
  status: string;
  valor: number;
  observacoes?: string;
  created_at: string;
  updated_at: string;
}

export interface LaboratorioInput {
  paciente: string;
  procedimento: string;
  laboratorio: string;
  data_envio: string;
  data_retorno: string;
  status: string;
  valor: number;
  observacoes?: string;
}

export function useLaboratorio() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: laboratorios = [], isLoading } = useQuery({
    queryKey: ["laboratorio", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const data = await apiList<Laboratorio>("laboratorio");
      return data.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    },
    enabled: !!user?.id,
  });

  const createLaboratorio = useMutation({
    mutationFn: async (input: LaboratorioInput) => {
      if (!user?.id) throw new Error("User not authenticated");

      return apiCreate<Laboratorio>("laboratorio", input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["laboratorio"] });
      toast({
        title: "Serviço cadastrado",
        description: "O serviço foi cadastrado com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao cadastrar",
        description: "Não foi possível cadastrar o serviço.",
        variant: "destructive",
      });
    },
  });

  const updateLaboratorio = useMutation({
    mutationFn: async ({ id, ...input }: LaboratorioInput & { id: string }) => {
      return apiUpdate<Laboratorio>("laboratorio", id, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["laboratorio"] });
      toast({
        title: "Serviço atualizado",
        description: "O serviço foi atualizado com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar o serviço.",
        variant: "destructive",
      });
    },
  });

  const deleteLaboratorio = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete("laboratorio", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["laboratorio"] });
      toast({
        title: "Serviço excluído",
        description: "O serviço foi excluído com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao excluir",
        description: "Não foi possível excluir o serviço.",
        variant: "destructive",
      });
    },
  });

  return {
    laboratorios,
    isLoading,
    createLaboratorio,
    updateLaboratorio,
    deleteLaboratorio,
  };
}
